import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Info } from 'lucide-react'
import React, { useEffect, useState } from 'react'

import { GettingBenchmarksFailed } from './GetErrorBanners'
import { type BenchmarkResult } from './types'
import { capitalize } from '../../utils'
import { useGetBenchmarks } from '../hooks/useGetBenchmarks'
import TanstackTable, {
  DEFAULT_PAGE_SIZE,
  type PaginationMetadata,
} from '../reusable/table/TanstackTable'
import TooltipWrapper from '../reusable/TooltipWrapper'

function BenchmarkCell ({ benchmark }: { benchmark: BenchmarkResult }) {
  return (
    <div className='flex items-center gap-x-1'>
      <span>
        {capitalize(benchmark.metal_tier)} {benchmark.coverage_percentile}
      </span>
      {benchmark.optimal_benchmark && (
        <TooltipWrapper
          tooltipText='This benchmark most cost-effectively resembles your current group plan.'
          buttonClasses='btn-link'
        >
          <Info size={16} />
        </TooltipWrapper>
      )}
    </div>
  )
}

const columnHelper = createColumnHelper<BenchmarkResult>()
const columns = [
  columnHelper.accessor(
    (row) => ({
      content: <BenchmarkCell benchmark={row} />,
    }),
    {
      header: 'Benchmark',
      id: 'benchmark',
      cell: (info) => info.getValue().content,
    }
  ),
  columnHelper.accessor('cost_per_employee', {
    header: 'Cost per employee',
    cell: (info) => `$${Number(info.getValue()).toFixed(2)}`,
  }),
  columnHelper.accessor('percent_employees_covered', {
    header: '% Covered employees',
    cell: (info) => `${info.getValue()}%`,
  }),
  columnHelper.accessor('incremental_cost_per_employee', {
    header: 'Incremental cost per newly covered employee',
    cell: (info) => `$${Number(info.getValue()).toFixed(2)}`,
  }),
  columnHelper.accessor('newly_covered_employees', {
    header: 'Newly covered employees',
  }),
]

export function BenchmarkList ({
  prospectId,
  quoteId,
}: {
  prospectId: string;
  quoteId: string;
}) {
  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const { data, isLoading, isError, error } = useGetBenchmarks({
    prospectId,
    quoteId,
    pagination,
  })

  const table = useReactTable({
    data: data?.data || [],
    columns,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    state: {
      pagination,
    },
  })

  useEffect(() => {
    setPaginationMetadata(data?.pagination)
  }, [data?.pagination])

  if (!isLoading && isError) {
    return (
      <div className='mb-6'>
        <GettingBenchmarksFailed errorObj={error} />
      </div>
    )
  }

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={isError}
      noDataMessage='No benchmarks found.'
      rowClasses={(row) => {
        return row.original.optimal_benchmark
          ? 'text-thatch-purple border-ds-border-primary-focus-ring border-t border-b'
          : ''
      }}
      paginationLabelSingular='benchmark'
      paginationMetadata={paginationMetadata}
      onNextPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.next_page || 0,
        })}
      onPreviousPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.prev_page || 0,
        })}
    />
  )
}
